import { CM_PER_M } from '../scene3d/units';

/**
 * Draw shadow polygons onto a Fabric.js render context (#605).
 *
 * Consumes the weighted MultiPolygons from `computeShadowGeometry` (world
 * meters) and paints them semi-transparently in canvas space, so the shadow
 * aligns with the garden objects at any zoom/pan. Meant to be called from a
 * Fabric.js `after:render` handler — the shadow lies over the objects (visible
 * even across filled areas like Rasen/Weg), analytically answering "where does
 * the sun reach".
 *
 * Mirrors drawViewportGrid's transform handling: `ctx.setTransform` with the
 * retina scaling times the viewportTransform, so drawing happens in canvas-space
 * coordinates on High-DPI screens too.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {import('fabric').Canvas} canvas
 * @param {Array<{weight:number, multiPolygon:number[][][][]}>} groups
 * @param {Object} [options]
 * @param {number} [options.pixelsPerCm=1] - editor scale (1 px = 1 cm)
 * @param {number} [options.baseOpacity=0.35] - opacity of a full-weight shadow
 * @param {string} [options.color='54, 66, 92'] - RGB triplet (calm grey-blue)
 */
export function drawShadowGroups(
  ctx,
  canvas,
  groups,
  { pixelsPerCm = 1, baseOpacity = 0.35, color = '54, 66, 92' } = {}
) {
  if (!ctx || !canvas || !groups || groups.length === 0) return;

  const vpt = canvas.viewportTransform;
  if (!vpt) return;

  const retina =
    typeof canvas.getRetinaScaling === 'function' ? canvas.getRetinaScaling() : 1;
  const mToPx = CM_PER_M * pixelsPerCm; // world meters → canvas pixels

  ctx.save();
  ctx.setTransform(
    retina * vpt[0],
    retina * vpt[1],
    retina * vpt[2],
    retina * vpt[3],
    retina * vpt[4],
    retina * vpt[5]
  );

  for (const group of groups) {
    ctx.beginPath();
    for (const polygon of group.multiPolygon) {
      for (const ring of polygon) {
        for (let i = 0; i < ring.length; i += 1) {
          const px = ring[i][0] * mToPx;
          const py = ring[i][1] * mToPx;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
      }
    }
    // evenodd lässt Löcher (Innenringe) frei; unierte Gruppen überlappen nicht
    ctx.fillStyle = `rgba(${color}, ${baseOpacity * group.weight})`;
    ctx.fill('evenodd');
  }

  ctx.restore();
}
